import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Avatar, Box, Button, Container, Typography } from '@mui/material';
import { IconContext } from 'react-icons';
import { TbCloudOff, TbRefresh } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import ProfileView from '../../Components/ProfileView/ProfileView';
import { useThemeColor } from '../../hooks/useThemeColor';
import './UserLinks.css';
import apiClient from '../../services/apiService';
import { getAvatarUrl, getBackground } from '../../utils';
import { BRAND_DARK_2, HTTP_NOT_FOUND, PROFILE_STATUS_NOT_FOUND, PROFILE_STATUS_OK } from '../../utils/constants';

function UserLinks() {
  const { t } = useTranslation();
  const { user } = useParams();
  const navigate = useNavigate();
  // Usernames are stored lowercase; normalize so casing in the URL doesn't matter.
  const normalizedUser = user?.toLowerCase();

  const [userFound, setUserFound] = useState<string | null>(null);
  const [userImage, setUserImage] = useState<any>(null);
  const [linksFound, setLinksFound] = useState<any[]>([]);
  const [themeFound, setThemeFound] = useState<any>({});
  const [infoFound, setInfoFound] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const handleRetry = useCallback(() => setRetryToken((token) => token + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);

    apiClient.get(`/profile/get-profile/${normalizedUser}`)
      .then(function (response) {
        if (cancelled) return;
        const { data } = response;
        if (response.status === 200 && data.status === PROFILE_STATUS_OK) {
          setUserFound(data.result.username);
          setThemeFound(data.result.theme);
          setUserImage(data.result.userImage);
          setInfoFound(data.result.info);
          const linksBefore = [...data.result.links].sort((a, b) => a.position - b.position);
          setLinksFound(linksBefore);
        } else {
          setLoadError(true);
        }
      })
      .catch(function (error) {
        if (cancelled) return;
        if (error?.response?.status === HTTP_NOT_FOUND
            || error?.response?.data?.status === PROFILE_STATUS_NOT_FOUND) {
          navigate('/404', { replace: true, state: { from: 'profile', userNotFound: normalizedUser } });
          return;
        }
        setLoadError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [normalizedUser, navigate, retryToken]);

  // The shared ScrollToTop fires once on route change, before this page's async
  // content grows — so the scroll can settle at the bottom. Reset on mount and
  // again once the profile is loaded, when the final height is known.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loading]);

  const background = getBackground(themeFound?.background);
  // Solid colour for the browser chrome (theme-color) and overscroll backdrop.
  // A gradient can't paint those areas, so we derive a representative colour
  // from the theme: the solid fill, or the gradient's starting (top) colour.
  // theme-color can't be a gradient: solid themes use their own colour, gradient
  // themes fall back to the Linkerto dark so the browser chrome stays coherent.
  const bgConfig = themeFound?.background;
  const chromeColor = bgConfig?.type === 'solid'
    ? bgConfig?.solid
    : BRAND_DARK_2;

  // Document-shell styling (outside React): browser chrome + overscroll, and the
  // user's themed background exposed as --lk-page-bg for .user-links-layout.
  useThemeColor(chromeColor, background, loading);

  return (
    <div className={`section-links ${loading ? '' : 'is-ready'}`}>
      <Helmet>
        <title>{userFound ? `Hi! this is ${userFound}` : t('error-page.title')}</title>
      </Helmet>
      <Container className="section-links-body" maxWidth="sm">
        {loadError ? (
          <Box className="user-links-error">
            <IconContext.Provider value={{ color: '#ffffff80', className: 'react-icons-color' }}>
              <TbCloudOff fontSize={50} />
            </IconContext.Provider>
            <Typography variant="h6" className="user-links-error-title">
              {t('error-page.load-error-title')}
            </Typography>
            <Typography variant="body2" className="user-links-error-subtitle">
              {t('error-page.load-error-subtitle')}
            </Typography>
            <Button
              variant="outlined"
              className="user-links-error-retry"
              startIcon={<TbRefresh />}
              onClick={handleRetry}
            >
              {t('error-page.retry')}
            </Button>
          </Box>
        ) : (
          <ProfileView
            className="user-links"
            links={linksFound}
            theme={themeFound}
            info={infoFound}
            username={userFound}
            origin="public"
            loading={loading}
            paintBackground={false}
            avatar={userImage ? <Avatar src={getAvatarUrl(userImage)} /> : undefined}
          />
        )}
      </Container>
    </div>
  );
}

export default UserLinks;
